import { config } from './config'
import './index.css'
import React, { useEffect, useState, lazy, Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter as Router, Route, Routes, useParams } from 'react-router-dom'
import { initializeIcons } from '@fluentui/react';
import 'bootstrap/dist/css/bootstrap.min.css';
import Layout from './pages/layout/Layout';
import BotChat from './pages/bot/bot';
import NoPage from './pages/NoPage';
import ProjectInsight from './pages/projectInsight/projectInsight';
import { AppStateProvider } from './state/AppProvider';
import { app } from "@microsoft/teams-js";

const HvProjectPage = lazy(() => import('./pages/hvProject/HvProjectPage'));
import { PublicClientApplication, Configuration, IPublicClientApplication} from "@azure/msal-browser";
import { MsalProvider, useMsal } from "@azure/msal-react";
import { initTelemetry, trackAuthEvent, trackAuthException } from './utils/telemetry';
import { bootstrapTeamsSession,isTeamsToken } from './utils/teamsAuth';

const redirectUri = window.location.origin
// Initialise App Insights as early as possible — before any React rendering.
initTelemetry();

initializeIcons("https://res.cdn.office.net/files/fabric-cdn-prod_20240129.001/assets/icons/")

console.log(redirectUri)

// Check if running in iframe BEFORE initializing MSAL
const _isInIframe = (): boolean => {
  try { return window.self !== window.top } catch { return true }
}

const msalConfig: Configuration = {
  auth: {
    clientId: config.env.REACT_APP_CLIENT_ID,
    authority: config.env.REACT_APP_AUTHORITY,
    redirectUri: redirectUri
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false
  }
}

// Only initialize MSAL if NOT in iframe — prevents X-Frame-Options errors when embedded
export const msalInstance = !_isInIframe() ? new PublicClientApplication(msalConfig) : null

const initializeMsal = async () => {
  if (!msalInstance) {
    console.warn('[IframeAuth] MSAL not initialized in iframe context')
    return
  }
  try {
    await msalInstance.initialize();
    console.log("MSAL initialized successfully.");
    trackAuthEvent("msal_init_success", { auth_path: "msal" });
  } catch (error) {
    console.error("MSAL initialization error:", error);
    trackAuthException(error, { auth_path: "msal", stage: "msal_init" });
  }
}
const initializeTeams = async () => {
  try {
    await app.initialize();
    const context = await app.getContext();
    const hostName = context.app.host.name;
    const isTeamsEnvironment = hostName === "Teams" || hostName === "TeamsModern";
    if (isTeamsEnvironment) {
      app.notifyAppLoaded();
      app.notifySuccess();
      trackAuthEvent("teams_init_success", { auth_path: "teams", host_name: hostName });
      return true;
    } else {
      trackAuthEvent("teams_init_non_teams_host", { auth_path: "teams", host_name: hostName });
      return false;
    }
  } catch (error: any) {
    // The Teams JS SDK always throws when called outside a Teams host (browser, Outlook, etc.).
    // Common expected messages: "No Parent window found", "Initialization Failed", "SDK initialization timed out".
    // These are NOT real errors — they are the normal detection mechanism for "not running in Teams".
    const msg: string = (error?.message ?? String(error)).toLowerCase();
    const isExpectedNonTeams =
      msg.includes("no parent window") ||
      msg.includes("initialization failed") ||
      msg.includes("sdk initialization timed out") ||
      msg.includes("not initialized");

    if (isExpectedNonTeams) {
      // Log as informational — browser path is normal
      trackAuthEvent("teams_not_available_browser", {
        auth_path: "browser",
        reason: error?.message ?? String(error),
      });
    } else {
      // Only treat as a genuine error if the message is unexpected
      trackAuthException(error, { auth_path: "teams", stage: "teams_init" });
    }
    return false;
  }
}

const initializeApp = async (): Promise<boolean> => {
  const isTeams = await initializeTeams()
  if (!isTeams) {
    await initializeMsal()
  }
  return isTeams
}

const initializeTeamsApp = async () => {
  const result = await initializeApp()
  return result
}

const handleMsalLogin = async (instance: IPublicClientApplication): Promise<boolean> => {
  try {
    // CRITICAL: Prevent MSAL redirects in iframe — they will fail with X-Frame-Options: deny
    if (_isInIframe()) {
      console.warn('[IframeAuth] handleMsalLogin called in iframe — returning false to prevent redirect.')
      return false
    }

    // 1. MUST await the redirect promise first.
    // This is what processes the code from the URL after the redirect.
    const loginResponse = await instance.handleRedirectPromise()

    if (loginResponse) {
      console.log('Successfully caught redirect response:', loginResponse.account)
      instance.setActiveAccount(loginResponse.account)
      return true
    }

    // 2. If no redirect response, check if we have a session in storage
    const currentAccounts = instance.getAllAccounts()
    if (currentAccounts.length > 0) {
      console.log('Account found in cache')
      instance.setActiveAccount(currentAccounts[0])
      return true
    }

    // 3. Last resort: Trigger login, but ONLY if no interaction is currently happening
    // Check the internal state of the instance
    const accounts = instance.getAllAccounts()
    if (accounts.length === 0) {
      console.log('No session found, starting loginRedirect...')
      await instance.loginRedirect({
        scopes: [`${config.env.REACT_APP_CLIENT_ID}/access_as_user`],
        // Force the redirect URI to be exactly what's in Azure
        redirectUri: window.location.origin
      })
    }

    return false
  } catch (error: any) {
    // This ignores the error if a redirect is already moving the page
    if (error.errorCode === 'interaction_in_progress') return false
    console.error('MSAL Login Error:', error)
    return false
  }
}

const extractTokenExpiry = (token: string): number => {
  try {
    const decoded = JSON.parse(atob(token.split('.')[1]))
    return decoded.exp * 1000
  } catch (e) {
    console.error('[IframeAuth] Failed to extract token expiry:', e)
    return 0
  }
}

// Reads the token injected by the Akira shell via the ?akiraToken= URL param.
// Call this from any API/auth utility instead of acquireTokenSilent when in iframe.
export const getInjectedToken = (): string | null =>
  sessionStorage.getItem('akira_injected_token')

const initializeApplication = async (
  instance: IPublicClientApplication | null,
  setIsTeams: (value: boolean) => void,
  setIsInitialized: (value: boolean) => void
) => {
  console.log('[InitApp] initializeApplication called. isInIframe:', _isInIframe(), 'instance:', instance ? 'exists' : 'null')
  // In iframe context, MSAL redirects are blocked — always skip regardless of token state
  if (_isInIframe()) {
    // Wait for token via postMessage from parent (sent by App component listener)
    const maxAttempts = 50
    let attempts = 0
    
    while (!sessionStorage.getItem('akira_injected_token') && attempts < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, 100))
      attempts++
    }
    
    if (attempts >= maxAttempts) {
      console.error('[IframeAuth] ❌ No token received from parent after waiting')
    } else {
      console.log('[IframeAuth] ✅ Token received from parent via postMessage')
    }
    
    setIsTeams(false)
    setIsInitialized(true)
    return
  }

  const isTeams = await initializeTeamsApp()
  setIsTeams(isTeams)

  let successfulInit = isTeams
  if (!isTeams) {
    successfulInit = instance ? await handleMsalLogin(instance) : false
  }
  setIsInitialized(successfulInit)
}

// Component to handle conditional routing based on agent parameter
const AgentRouter = ({ isTeams }: { isTeams: boolean }) => {
  const { agent } = useParams<{ agent: string }>()
  console.log('Agent:', agent)

  // Only route to ProjectInsight for 'projectinsight' agent
  if (agent === 'projectinsight') {
    return <ProjectInsight isTeams={isTeams} />
  }

  // For all other agents (trafochat, pgtr-engineering, etc.), route to BotChat
  return <BotChat isTeams={isTeams} />
}

// Custom hook to safely use MSAL, with fallback for iframe context
const useMsalSafe = () => {
  try {
    return useMsal()
  } catch {
    // In iframe context or when MsalProvider is not present
    console.warn('[IframeAuth] useMsal() failed — likely in iframe without MsalProvider')
    return { instance: null, inProgress: 'none' as any }
  }
}

const App = () => {
  const { instance, inProgress } = useMsalSafe()
  const [isTeams, setIsTeams] = useState(false)
  const [isInitialized, setIsInitialized] = useState(false)

  // Add message listener for iframe token from parent
  useEffect(() => {
    const handlePostMessage = async (event: MessageEvent) => {
      if (event.data?.type === 'akira-token' && event.data?.token) {
        const injectedToken = event.data.token
        const expiry = extractTokenExpiry(injectedToken)

        // Determine if the injected token is a Teams SSO token or a regular web token
        if(!isTeamsToken(injectedToken)){
          console.info('[IframeAuth:index] Web Token received skipping bootstrap authentication')
          sessionStorage.setItem('akira_injected_token', injectedToken);
          sessionStorage.setItem('akira_raw_token_expiry', expiry.toString())
        }else{
          console.info('[IframeAuth:index] Teams token received, bootstrapping backend session')
          const result = await bootstrapTeamsSession(injectedToken)
          console.log("result:", result)
          if (result.ok && result.accessToken) {
            sessionStorage.setItem('akira_injected_token', result.accessToken)
            sessionStorage.setItem('akira_token_expiry', String((result.expiresAt ?? 0) * 1000))
            console.log('[IframeAuth] ✅ Teams backend session established')
          } else {
            console.error('[IframeAuth] ❌ Teams backend session bootstrap failed')
          }

        }
        console.log('[IframeAuth:index] Received akira-token via postMessage')
        console.log('🔑 [IframeAuth:index] Token stored, expires at:', new Date(expiry).toISOString())
      }
    }
    
    window.addEventListener('message', handlePostMessage)
    return () => window.removeEventListener('message', handlePostMessage)
  }, [])

  useEffect(() => {
    if (inProgress === 'none') {
      initializeApplication(instance, setIsTeams, setIsInitialized)
    }
  }, [instance, inProgress])

  if (inProgress !== 'none' || !isInitialized) {
    return <div>Loading...</div>
  }

  return (
    <AppStateProvider>
      <Router>
        <Routes>
          <Route path="/" element={<Layout />}>
            <Route path="/agent/hvproject" element={<Suspense fallback={<div>Loading HV Project…</div>}><HvProjectPage /></Suspense>} />
            <Route index path="/agent/:agent" element={<AgentRouter isTeams={isTeams}/>} />
            <Route index path="/agent/:agent/:clientSource" element={<AgentRouter isTeams={isTeams} />} />
            <Route index element={<BotChat isTeams={isTeams} />} />
            <Route path="*" element={<NoPage />} />
          </Route>
        </Routes>
      </Router>
    </AppStateProvider>
  )
}

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <React.StrictMode>
    {msalInstance ? (
      <MsalProvider instance={msalInstance}>
        <App />
      </MsalProvider>
    ) : (
      // In iframe context, render App directly without MSAL provider
      // useMsal() hook will be skipped/guarded in App component
      <App />
    )}
  </React.StrictMode>
)
